/**
 * Site Theme CSS Variable Generation
 * Mirrors the logic in mf-portal-editor-v2/src/utils/themingService.ts
 * Generates CSS variables for site-specific theme values
 */

import type { SiteTheme } from './apiClient';

// Default theme values (fallbacks)
const DEFAULT_SITE_THEME = {
  fontFamily: 'ui-sans-serif, system-ui, sans-serif',
  primaryColor: '#3b82f6',
  secondaryColor: '#3b44f6',
  textMutedColor: '#626f84',
  backgroundTintColor: '#f7f7f8',
  backgroundColor: '#ffffff',
  textColor: '#1f2937',
  fontSize: {
    small: '14px',
    medium: '16px',
    large: '20px',
  },
  spacing: {
    small: '8px',
    medium: '16px',
    large: '24px',
  },
} as const;

/**
 * Helper to get contrast color (white or black) for a given hex color
 */
const getContrastColor = (
  hexColor: string | undefined,
  fallback = '#ffffff',
): string => {
  if (!hexColor) return fallback;
  try {
    const hex = hexColor.replace('#', '');
    const r = Number.parseInt(hex.substring(0, 2), 16);
    const g = Number.parseInt(hex.substring(2, 4), 16);
    const b = Number.parseInt(hex.substring(4, 6), 16);
    const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
    return luminance > 0.5 ? '#000000' : '#ffffff';
  } catch {
    return fallback;
  }
};

/**
 * Helper to convert hex to rgba
 */
const hexToRgba = (
  hex: string | undefined,
  alpha: number,
  fallback = '',
): string => {
  if (!hex) return fallback;
  try {
    const cleanHex = hex.replace('#', '');
    const r = Number.parseInt(cleanHex.substring(0, 2), 16);
    const g = Number.parseInt(cleanHex.substring(2, 4), 16);
    const b = Number.parseInt(cleanHex.substring(4, 6), 16);
    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
  } catch {
    return fallback || hex;
  }
};

interface HslColor {
  h: number;
  s: number;
  l: number;
}

const normalizeHex = (hex: string): string | null => {
  if (!hex || typeof hex !== 'string') return null;
  const value = hex.trim().toLowerCase();
  if (!value.startsWith('#')) return null;
  const raw = value.slice(1);
  if (raw.length === 3 && /^[0-9a-f]{3}$/.test(raw)) {
    return `#${raw
      .split('')
      .map(ch => ch + ch)
      .join('')}`;
  }
  if (raw.length === 6 && /^[0-9a-f]{6}$/.test(raw)) {
    return `#${raw}`;
  }
  return null;
};

const hexToHsl = (hexColor: string): HslColor | null => {
  const normalized = normalizeHex(hexColor);
  if (!normalized) return null;

  const r = Number.parseInt(normalized.slice(1, 3), 16) / 255;
  const g = Number.parseInt(normalized.slice(3, 5), 16) / 255;
  const b = Number.parseInt(normalized.slice(5, 7), 16) / 255;

  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const delta = max - min;

  let h = 0;
  const l = (max + min) / 2;
  const s = delta === 0 ? 0 : delta / (1 - Math.abs(2 * l - 1));

  if (delta !== 0) {
    if (max === r) h = 60 * (((g - b) / delta) % 6);
    else if (max === g) h = 60 * ((b - r) / delta + 2);
    else h = 60 * ((r - g) / delta + 4);
  }
  if (h < 0) h += 360;

  return { h, s: s * 100, l: l * 100 };
};

const hslToHex = (h: number, s: number, l: number): string => {
  const hue = ((h % 360) + 360) % 360;
  const sat = Math.max(0, Math.min(100, s)) / 100;
  const light = Math.max(0, Math.min(100, l)) / 100;

  const c = (1 - Math.abs(2 * light - 1)) * sat;
  const x = c * (1 - Math.abs(((hue / 60) % 2) - 1));
  const m = light - c / 2;

  let r = 0;
  let g = 0;
  let b = 0;
  if (hue < 60) {
    r = c;
    g = x;
    b = 0;
  } else if (hue < 120) {
    r = x;
    g = c;
    b = 0;
  } else if (hue < 180) {
    r = 0;
    g = c;
    b = x;
  } else if (hue < 240) {
    r = 0;
    g = x;
    b = c;
  } else if (hue < 300) {
    r = x;
    g = 0;
    b = c;
  } else {
    r = c;
    g = 0;
    b = x;
  }

  const toHex = (n: number) =>
    Math.round((n + m) * 255)
      .toString(16)
      .padStart(2, '0');
  return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
};

const derivePaletteFromPrimary = (
  primaryColor: string,
): {
  secondaryColor: string;
  textMutedColor: string;
  backgroundTintColor: string;
} | null => {
  const hsl = hexToHsl(primaryColor);
  if (!hsl) return null;

  return {
    secondaryColor: hslToHex((hsl.h + 20) % 360, hsl.s, hsl.l),
    textMutedColor: hslToHex(hsl.h, Math.min(hsl.s * 0.2, 15), 45),
    backgroundTintColor: hslToHex(hsl.h, Math.min(hsl.s * 0.1, 8), 97),
  };
};

/**
 * Generate CSS variable declarations for site theme
 * Returns a string of CSS variable declarations for :root
 */
export const generateSiteThemeCssVariables = (
  theme: SiteTheme | null | undefined,
): string => {
  if (!theme) return '';

  const primaryColor = theme.primaryColor || DEFAULT_SITE_THEME.primaryColor;
  const derived = derivePaletteFromPrimary(primaryColor);
  const secondaryColor =
    theme.secondaryColor ||
    derived?.secondaryColor ||
    DEFAULT_SITE_THEME.secondaryColor;
  const textMutedColor =
    theme.textMutedColor ||
    derived?.textMutedColor ||
    DEFAULT_SITE_THEME.textMutedColor;
  const backgroundTintColor =
    theme.backgroundTintColor ||
    derived?.backgroundTintColor ||
    DEFAULT_SITE_THEME.backgroundTintColor;

  const cssVars: string[] = [];

  const addVar = (
    name: string,
    value: string | undefined,
    fallback: string,
  ) => {
    cssVars.push(`${name}: ${value || fallback};`);
  };

  // Layer 1: Site-specific (plain CSS)
  addVar('--site-primary', primaryColor, DEFAULT_SITE_THEME.primaryColor);
  addVar('--site-secondary', secondaryColor, DEFAULT_SITE_THEME.secondaryColor);
  addVar(
    '--site-text-muted',
    textMutedColor,
    DEFAULT_SITE_THEME.textMutedColor,
  );
  addVar(
    '--site-bg-tint',
    backgroundTintColor,
    DEFAULT_SITE_THEME.backgroundTintColor,
  );
  addVar(
    '--site-bg',
    theme.backgroundColor,
    DEFAULT_SITE_THEME.backgroundColor,
  );
  addVar('--site-text', theme.textColor, DEFAULT_SITE_THEME.textColor);
  addVar('--site-font', theme.fontFamily, DEFAULT_SITE_THEME.fontFamily);
  addVar(
    '--site-font-sm',
    theme.fontSize?.small,
    DEFAULT_SITE_THEME.fontSize.small,
  );
  addVar(
    '--site-font-md',
    theme.fontSize?.medium,
    DEFAULT_SITE_THEME.fontSize.medium,
  );
  addVar(
    '--site-font-lg',
    theme.fontSize?.large,
    DEFAULT_SITE_THEME.fontSize.large,
  );
  addVar(
    '--site-spacing-sm',
    theme.spacing?.small,
    DEFAULT_SITE_THEME.spacing.small,
  );
  addVar(
    '--site-spacing-md',
    theme.spacing?.medium,
    DEFAULT_SITE_THEME.spacing.medium,
  );
  addVar(
    '--site-spacing-lg',
    theme.spacing?.large,
    DEFAULT_SITE_THEME.spacing.large,
  );

  // Layer 2: shadcn/ui conventions
  addVar('--primary', primaryColor, DEFAULT_SITE_THEME.primaryColor);
  addVar('--primary-foreground', getContrastColor(primaryColor), '#ffffff');
  addVar('--secondary', secondaryColor, DEFAULT_SITE_THEME.secondaryColor);
  addVar(
    '--background',
    theme.backgroundColor,
    DEFAULT_SITE_THEME.backgroundColor,
  );
  addVar('--foreground', theme.textColor, DEFAULT_SITE_THEME.textColor);
  addVar('--muted', backgroundTintColor, '#64748b33');
  addVar(
    '--muted-foreground',
    textMutedColor,
    DEFAULT_SITE_THEME.secondaryColor,
  );
  addVar('--accent', backgroundTintColor, '#3b82f61a');
  addVar(
    '--accent-foreground',
    theme.primaryColor,
    DEFAULT_SITE_THEME.primaryColor,
  );
  addVar(
    '--border',
    hexToRgba(theme.textColor || DEFAULT_SITE_THEME.textColor, 0.1),
    '#1f29371a',
  );
  addVar('--ring', primaryColor, DEFAULT_SITE_THEME.primaryColor);
  addVar('--font-sans', theme.fontFamily, DEFAULT_SITE_THEME.fontFamily);

  // Layer 3: Tailwind v4 semantic
  addVar('--color-primary', primaryColor, DEFAULT_SITE_THEME.primaryColor);
  addVar(
    '--color-secondary',
    secondaryColor,
    DEFAULT_SITE_THEME.secondaryColor,
  );
  addVar(
    '--color-muted-foreground',
    textMutedColor,
    DEFAULT_SITE_THEME.textMutedColor,
  );
  addVar(
    '--color-background-tint',
    backgroundTintColor,
    DEFAULT_SITE_THEME.backgroundTintColor,
  );
  addVar(
    '--color-background',
    theme.backgroundColor,
    DEFAULT_SITE_THEME.backgroundColor,
  );
  addVar('--color-foreground', theme.textColor, DEFAULT_SITE_THEME.textColor);
  addVar('--font-family-sans', theme.fontFamily, DEFAULT_SITE_THEME.fontFamily);
  addVar(
    '--font-size-sm',
    theme.fontSize?.small,
    DEFAULT_SITE_THEME.fontSize.small,
  );
  addVar(
    '--font-size-base',
    theme.fontSize?.medium,
    DEFAULT_SITE_THEME.fontSize.medium,
  );
  addVar(
    '--font-size-lg',
    theme.fontSize?.large,
    DEFAULT_SITE_THEME.fontSize.large,
  );
  addVar('--spacing-2', theme.spacing?.small, DEFAULT_SITE_THEME.spacing.small);
  addVar(
    '--spacing-4',
    theme.spacing?.medium,
    DEFAULT_SITE_THEME.spacing.medium,
  );
  addVar('--spacing-6', theme.spacing?.large, DEFAULT_SITE_THEME.spacing.large);

  return `:root {
  ${cssVars.join('\n  ')}
}

/* ═══════════════════════════════════════════════════════════════════════════
   SITE FONT APPLICATION
   Apply site theme font to all elements
   ═══════════════════════════════════════════════════════════════════════════ */

/* Common text elements inherit from body */
/* p, span, div, li, td, th, label, a, button, input, textarea, select {
  font-family: inherit;
} */
/* Explicit .font-site class */
.font-site, [class*="font-site"] {
  font-family: var(--site-font, ui-sans-serif, system-ui, sans-serif);
}`;
};

/**
 * Generate semantic CSS fallback classes for Tailwind CDN
 * Uses high specificity selectors to override Tailwind CDN defaults
 * The [class] attribute selector adds specificity without changing functionality
 */
export const generateSemanticCssFallback = (
  theme?: SiteTheme | null,
): string => {
  // Use actual theme values directly to guarantee they apply
  const primary = theme?.primaryColor || '#3b82f6';
  const derived = derivePaletteFromPrimary(primary);
  const secondary =
    theme?.secondaryColor || derived?.secondaryColor || '#64748b';
  const textMuted =
    theme?.textMutedColor || derived?.textMutedColor || secondary;
  const backgroundTint =
    theme?.backgroundTintColor || derived?.backgroundTintColor || '#f1f5f9';
  const background = theme?.backgroundColor || '#ffffff';
  const primaryForeground = theme?.primaryForeground || '#ffffff';
  const foreground = theme?.textColor || '#1f2937';
  const fontFamily =
    theme?.fontFamily || 'ui-sans-serif, system-ui, sans-serif';

  return `/* ═══════════════════════════════════════════════════════════════════════════
   SEMANTIC COLOR CLASSES - High Specificity Override for Tailwind CDN
   Uses doubled selectors and !important to guarantee site theme colors apply
   ═══════════════════════════════════════════════════════════════════════════ */

/* Background colors - using doubled selector for higher specificity */
body .bg-primary.bg-primary, body [class~="bg-primary"] { background-color: ${primary} !important; }
body .bg-secondary.bg-secondary, body [class~="bg-secondary"] { background-color: ${secondary} !important; }
body .bg-background.bg-background, body [class~="bg-background"] { background-color: ${background} !important; }
body .bg-muted.bg-muted, body [class~="bg-muted"] { background-color: ${backgroundTint} !important; }
body .bg-accent.bg-accent, body [class~="bg-accent"] { background-color: ${backgroundTint} !important; }
body .bg-destructive.bg-destructive, body [class~="bg-destructive"] { background-color: #ef4444 !important; }

/* Site-specific background colors */
body .bg-site-primary, body [class~="bg-site-primary"] { background-color: ${primary} !important; }
body .bg-site-secondary, body [class~="bg-site-secondary"] { background-color: ${secondary} !important; }
body .bg-site-bg, body [class~="bg-site-bg"] { background-color: ${background} !important; }

/* Text colors */
body .text-primary.text-primary, body [class~="text-primary"] { color: ${primary} !important; }
body .text-primary-foreground.text-primary-foreground, body [class~="text-primary-foreground"] { color: ${primaryForeground} !important; }
body .text-secondary.text-secondary, body [class~="text-secondary"] { color: ${secondary} !important; }
body .text-secondary-foreground.text-secondary-foreground, body [class~="text-secondary-foreground"] { color: ${foreground} !important; }
body .text-foreground.text-foreground, body [class~="text-foreground"] { color: ${foreground} !important; }
body .text-muted-foreground.text-muted-foreground, body [class~="text-muted-foreground"] { color: ${textMuted} !important; }
body .text-accent-foreground.text-accent-foreground, body [class~="text-accent-foreground"] { color: ${foreground} !important; }
body .text-destructive.text-destructive, body [class~="text-destructive"] { color: #ef4444 !important; }
body .text-destructive-foreground.text-destructive-foreground, body [class~="text-destructive-foreground"] { color: #ffffff !important; }

/* Site-specific text colors */
body .text-site-primary, body [class~="text-site-primary"] { color: ${primary} !important; }
body .text-site-secondary, body [class~="text-site-secondary"] { color: ${secondary} !important; }
body .text-site-text, body [class~="text-site-text"] { color: ${foreground} !important; }

/* Border colors */
body .border-primary.border-primary, body [class~="border-primary"] { border-color: ${primary} !important; }
body .border-secondary.border-secondary, body [class~="border-secondary"] { border-color: ${secondary} !important; }
body .border-border.border-border, body [class~="border-border"] { border-color: ${foreground} !important; }
body .border-input.border-input, body [class~="border-input"] { border-color: ${foreground} !important; }

/* Ring colors */
body .ring-primary.ring-primary, body [class~="ring-primary"] { --tw-ring-color: ${primary} !important; }
body .ring-ring.ring-ring, body [class~="ring-ring"] { --tw-ring-color: ${primary} !important; }

/* Font family */
body .font-site, body [class~="font-site"] { font-family: ${fontFamily} !important; }
body .font-sans.font-sans, body [class~="font-sans"] { font-family: ${fontFamily} !important; }

/* ─────────────────────────────────────────────────────────────────────────
   TYPOGRAPHY - Site theme font sizes
   ───────────────────────────────────────────────────────────────────────── */
body .text-site-sm, body [class~="text-site-sm"] { font-size: ${theme?.fontSize?.small || '14px'} !important; }
body .text-site-md, body .text-site-base, body [class~="text-site-md"], body [class~="text-site-base"] { font-size: ${theme?.fontSize?.medium || '16px'} !important; }
body .text-site-lg, body [class~="text-site-lg"] { font-size: ${theme?.fontSize?.large || '20px'} !important; }

/* ─────────────────────────────────────────────────────────────────────────
   SPACING - Site theme spacing utilities
   ───────────────────────────────────────────────────────────────────────── */
/* Padding */
body .p-site-sm, body [class~="p-site-sm"] { padding: ${theme?.spacing?.small || '8px'} !important; }
body .p-site-md, body [class~="p-site-md"] { padding: ${theme?.spacing?.medium || '16px'} !important; }
body .p-site-lg, body [class~="p-site-lg"] { padding: ${theme?.spacing?.large || '24px'} !important; }
body .px-site-sm, body [class~="px-site-sm"] { padding-left: ${theme?.spacing?.small || '8px'} !important; padding-right: ${theme?.spacing?.small || '8px'} !important; }
body .px-site-md, body [class~="px-site-md"] { padding-left: ${theme?.spacing?.medium || '16px'} !important; padding-right: ${theme?.spacing?.medium || '16px'} !important; }
body .px-site-lg, body [class~="px-site-lg"] { padding-left: ${theme?.spacing?.large || '24px'} !important; padding-right: ${theme?.spacing?.large || '24px'} !important; }
body .py-site-sm, body [class~="py-site-sm"] { padding-top: ${theme?.spacing?.small || '8px'} !important; padding-bottom: ${theme?.spacing?.small || '8px'} !important; }
body .py-site-md, body [class~="py-site-md"] { padding-top: ${theme?.spacing?.medium || '16px'} !important; padding-bottom: ${theme?.spacing?.medium || '16px'} !important; }
body .py-site-lg, body [class~="py-site-lg"] { padding-top: ${theme?.spacing?.large || '24px'} !important; padding-bottom: ${theme?.spacing?.large || '24px'} !important; }

/* Margin */
body .m-site-sm, body [class~="m-site-sm"] { margin: ${theme?.spacing?.small || '8px'} !important; }
body .m-site-md, body [class~="m-site-md"] { margin: ${theme?.spacing?.medium || '16px'} !important; }
body .m-site-lg, body [class~="m-site-lg"] { margin: ${theme?.spacing?.large || '24px'} !important; }
body .mx-site-sm, body [class~="mx-site-sm"] { margin-left: ${theme?.spacing?.small || '8px'} !important; margin-right: ${theme?.spacing?.small || '8px'} !important; }
body .mx-site-md, body [class~="mx-site-md"] { margin-left: ${theme?.spacing?.medium || '16px'} !important; margin-right: ${theme?.spacing?.medium || '16px'} !important; }
body .mx-site-lg, body [class~="mx-site-lg"] { margin-left: ${theme?.spacing?.large || '24px'} !important; margin-right: ${theme?.spacing?.large || '24px'} !important; }
body .my-site-sm, body [class~="my-site-sm"] { margin-top: ${theme?.spacing?.small || '8px'} !important; margin-bottom: ${theme?.spacing?.small || '8px'} !important; }
body .my-site-md, body [class~="my-site-md"] { margin-top: ${theme?.spacing?.medium || '16px'} !important; margin-bottom: ${theme?.spacing?.medium || '16px'} !important; }
body .my-site-lg, body [class~="my-site-lg"] { margin-top: ${theme?.spacing?.large || '24px'} !important; margin-bottom: ${theme?.spacing?.large || '24px'} !important; }

/* Gap (flexbox/grid) */
body .gap-site-sm, body [class~="gap-site-sm"] { gap: ${theme?.spacing?.small || '8px'} !important; }
body .gap-site-md, body [class~="gap-site-md"] { gap: ${theme?.spacing?.medium || '16px'} !important; }
body .gap-site-lg, body [class~="gap-site-lg"] { gap: ${theme?.spacing?.large || '24px'} !important; }

/* Space between */
body .space-y-site-sm > * + *, body [class~="space-y-site-sm"] > * + * { margin-top: ${theme?.spacing?.small || '8px'} !important; }
body .space-y-site-md > * + *, body [class~="space-y-site-md"] > * + * { margin-top: ${theme?.spacing?.medium || '16px'} !important; }
body .space-y-site-lg > * + *, body [class~="space-y-site-lg"] > * + * { margin-top: ${theme?.spacing?.large || '24px'} !important; }
body .space-x-site-sm > * + *, body [class~="space-x-site-sm"] > * + * { margin-left: ${theme?.spacing?.small || '8px'} !important; }
body .space-x-site-md > * + *, body [class~="
.space-x-site-lg > * + *, [class~="space-x-site-lg"] > * + * { margin-left: ${theme?.spacing?.large || '24px'} !important; }
`;
};

/**
 * Generate complete site theme style block (CSS variables + semantic fallback)
 */
export const generateSiteThemeStyleBlock = (
  theme: SiteTheme | null | undefined,
): string => {
  const cssVariables = generateSiteThemeCssVariables(theme);
  const semanticFallback = generateSemanticCssFallback(theme);

  if (!cssVariables) {
    return `<style id="tailwind-semantic-fallback">${semanticFallback}</style>`;
  }

  return `<style id="site-theme-variables">${cssVariables}</style>
<style id="tailwind-semantic-fallback">${semanticFallback}</style>`;
};

/**
 * Extract the primary font name from a font-family string
 * e.g., '"Instrument Sans", sans-serif' -> 'Instrument Sans'
 * e.g., '""Inter", sans-serif"' -> 'Inter'
 */
const extractFontName = (fontFamily: string | undefined): string | null => {
  if (!fontFamily) return null;

  // Remove all surrounding quotes and whitespace first
  let cleaned = fontFamily.trim();

  // Handle cases where the entire string is wrapped in quotes (from JSON stringification issues)
  while (
    (cleaned.startsWith('"') && cleaned.endsWith('"')) ||
    (cleaned.startsWith("'") && cleaned.endsWith("'"))
  ) {
    cleaned = cleaned.slice(1, -1).trim();
  }

  // Split by comma and get the first font
  const parts = cleaned.split(',');
  if (parts.length === 0) return null;

  // Clean up the first font name - remove quotes
  const fontName = parts[0]
    .trim()
    .replace(/^["']+|["']+$/g, '')
    .trim();

  // Skip generic fallbacks
  if (
    [
      'sans-serif',
      'serif',
      'monospace',
      'cursive',
      'fantasy',
      'system-ui',
    ].includes(fontName.toLowerCase())
  ) {
    return null;
  }

  return fontName || null;
};

/**
 * Generate Google Fonts URL from theming/brand data
 *
 * TODO: Consolidate Google Fonts URL generation to use shared fonts.ts export from mfDesignSystem
 * to ensure DRY principle across mf-portal-editor-v2 and mf-portal-modernjs-ssr projects.
 *
 * @param themingData Raw theming data object with font-*-family properties
 * @returns Google Fonts URL or null if no branded fonts found
 */
export const generateGoogleFontsUrl = (
  themingData: Record<string, unknown> | null | undefined,
): string | null => {
  if (!themingData) return null;

  // Support both direct access and nested 'light' section
  const light = (themingData.light as Record<string, unknown>) || themingData;

  // Extract font names
  const headingFont = extractFontName(light['font-heading-family'] as string);
  const bodyFont = extractFontName(light['font-body-family'] as string);

  if (!headingFont && !bodyFont) return null;

  // Extract weights (default to common weights if not specified)
  const headingWeight =
    (light['font-heading-weight'] as string) || '400;500;600;700';
  const bodyWeight = (light['font-body-weight'] as string) || '400;500;600;700';

  // Build font families for the URL
  const families: string[] = [];

  if (headingFont) {
    // Format: Inter:wght@400;500;600;700
    const weights = headingWeight.includes(';')
      ? headingWeight
      : '400;500;600;700';
    families.push(`${headingFont.replace(/\s+/g, '+')}:wght@${weights}`);
  }

  if (bodyFont && bodyFont !== headingFont) {
    const weights = bodyWeight.includes(';') ? bodyWeight : '400;500;600;700';
    families.push(`${bodyFont.replace(/\s+/g, '+')}:wght@${weights}`);
  }

  if (families.length === 0) return null;

  return `https://fonts.googleapis.com/css2?${families.map(f => `family=${f}`).join('&')}&display=swap`;
};

// System fonts that don't need to be loaded from Google Fonts
const SYSTEM_FONTS = [
  'arial',
  'helvetica',
  'times new roman',
  'times',
  'georgia',
  'verdana',
  'courier new',
  'courier',
  'trebuchet ms',
  'impact',
  'comic sans ms',
  'palatino linotype',
  'palatino',
  'lucida console',
  'lucida sans unicode',
  'tahoma',
  'geneva',
  'segoe ui',
  'system-ui',
  'sans-serif',
  'serif',
  'monospace',
];

/**
 * Generate Google Fonts URL for the site theme font (if it's a Google Font)
 *
 * TODO: Consolidate Google Fonts URL generation to use shared fonts.ts export from mfDesignSystem
 * to ensure DRY principle across mf-portal-editor-v2 and mf-portal-modernjs-ssr projects.
 *
 * @param fontFamily The font family from site theme (e.g., "Montserrat" or "Times New Roman")
 * @returns Google Fonts URL or null if system font
 */
export const generateSiteThemeFontUrl = (
  fontFamily: string | null | undefined,
): string | null => {
  if (!fontFamily) return null;

  // Extract primary font name
  const fontName = extractFontName(fontFamily);
  if (!fontName) return null;

  // Don't load system fonts from Google Fonts
  if (SYSTEM_FONTS.includes(fontName.toLowerCase())) return null;

  return `https://fonts.googleapis.com/css2?family=${fontName.replace(/\s+/g, '+')}:wght@400;500;600;700&display=swap`;
};

/**
 * Extract branding logos from theming data
 * @param themingData The theming data object (fetched from theming service)
 * @returns Object with desktop and mobile logo URLs
 */
export interface BrandingLogos {
  desktop: string | null;
  mobile: string | null;
}

export const getBrandingLogosFromTheming = (
  themingData: unknown,
): BrandingLogos => {
  try {
    const data = themingData as Record<string, unknown> | null;
    const light = data?.light as Record<string, unknown> | undefined;
    return {
      desktop: (light?.['logo-desktop-url'] as string) || null,
      mobile: (light?.['logo-mobile-url'] as string) || null,
    };
  } catch {
    return { desktop: null, mobile: null };
  }
};

/**
 * Generate CSS variable declarations for branding logos
 * @param themingData The theming data object
 * @returns CSS string with logo variable declarations
 */
export const generateBrandingLogoCssVariables = (
  themingData: unknown,
): string => {
  const logos = getBrandingLogosFromTheming(themingData);
  if (!logos.desktop && !logos.mobile) return '';

  const cssVars: string[] = [];

  if (logos.desktop) {
    cssVars.push(`--logo-desktop: url('${logos.desktop}');`);
    cssVars.push(`--logo-desktop-url: '${logos.desktop}';`);
  }
  if (logos.mobile) {
    cssVars.push(`--logo-mobile: url('${logos.mobile}');`);
    cssVars.push(`--logo-mobile-url: '${logos.mobile}';`);
  }

  return `:root {\n  ${cssVars.join('\n  ')}\n}`;
};

/**
 * Generate HTML data attributes for branding logos
 * Returns an object of data attribute values to add to <html> element
 */
export const getBrandingLogoDataAttributes = (
  themingData: unknown,
): Record<string, string> => {
  const logos = getBrandingLogosFromTheming(themingData);
  const attrs: Record<string, string> = {};

  if (logos.desktop) {
    attrs['data-logo-desktop'] = logos.desktop;
  }
  if (logos.mobile) {
    attrs['data-logo-mobile'] = logos.mobile;
  }

  return attrs;
};
